import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Modal from '../Modal';
import Button from '../Button';
import Toast from '../Toast';
import TenantUserService from '../../services/tenant-user.service';
import ImpersonationService from '../../services/impersonation.service';
import { ImpersonationContext } from '../../contexts/ImpersonationContext';

interface TenantUserOption {
  id: number;
  name: string;
}

interface ImpersonationStartModalProps {
  open: boolean;
  tenantId: number;
  tenantName: string;
  onClose: () => void;
}

const DURATION_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
];

const ImpersonationStartModal: React.FC<ImpersonationStartModalProps> = ({ open, tenantId, tenantName, onClose }) => {
  const { startSession } = useContext(ImpersonationContext);
  const [users, setUsers] = useState<TenantUserOption[]>([]);
  const [targetUser, setTargetUser] = useState<TenantUserOption | null>(null);
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState(30);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTargetUser(null);
    setReason('');
    setDuration(30);
    TenantUserService.list({ tenantId }).then((response: any) => {
      const rows = (response?.data?.items ?? response?.data ?? []).filter((u: any) => u.status === 'active');
      setUsers(rows.map((u: any) => ({ id: u.id, name: u.name })));
    });
  }, [open, tenantId]);

  const handleConfirm = async () => {
    if (!targetUser || !reason.trim()) return;
    setSubmitting(true);
    try {
      const response: any = await ImpersonationService.start({
        targetTenantId: tenantId,
        targetUserId: targetUser.id,
        reason: reason.trim(),
        durationMinutes: duration,
      });
      const data = response?.data;
      startSession({
        sessionId: data.sessionId,
        impersonationToken: data.impersonationToken,
        targetTenantId: tenantId,
        targetTenantName: tenantName,
        targetUserName: targetUser.name,
        expiresAt: data.expiresAt,
      });
      toast.success(<Toast message="Impersonation session started" />, { icon: false });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Impersonate User"
      maxWidth="sm"
      content={
        <div className="d-flex flex-column gap-3">
          <TextField label="Target Tenant" value={tenantName} disabled fullWidth size="small" />
          <Autocomplete
            options={users}
            getOptionLabel={(option) => option.name}
            value={targetUser}
            onChange={(_e, value) => setTargetUser(value)}
            renderInput={(params) => <TextField {...params} label="Target User" required size="small" />}
          />
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            multiline
            minRows={2}
            size="small"
          />
          <TextField
            select
            label="Session Duration"
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            size="small"
          >
            {DURATION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
      }
      action={
        <>
          <Button className="btn btn-outline-secondary" text="Cancel" onClick={onClose} />
          <Button
            className="btn btn-primary"
            text="Start Session"
            loading={submitting}
            disabled={!targetUser || !reason.trim()}
            onClick={handleConfirm}
          />
        </>
      }
    />
  );
};

export default ImpersonationStartModal;
